import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Input,
  InputAdornment,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Icons
import { DialogCloseCIcon, ContactSearchCIcon } from '~/assets/images';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import './styles.scss';

// Below this count the list is short enough that a search box is just noise
const SEARCH_THRESHOLD = 8;

const LinkedTemplatesModal = ({ open, templates = [], onClose }) => {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setSearch('');
    }
  }, [open]);

  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return templates;
    }
    return templates.filter(
      (template) =>
        String(template.id).includes(query) ||
        (template.title || '').toLowerCase().includes(query)
    );
  }, [templates, search]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: 'linkedTemplatesModal' }}
    >
      <DialogContent className="linkedTemplatesModalContent">
        <Box className="modalHeader">
          <Typography variant="h6" className="modalTitle">
            Tracking Number In Use
          </Typography>
          <IconButton onClick={onClose} className="closeButton">
            <img src={DialogCloseCIcon} alt="close" />
          </IconButton>
        </Box>

        <Typography component="p" className="modalSubtext">
          This number is used in {templates.length} template{templates.length === 1 ? '' : 's'}{' '}
          and can&apos;t be released until it&apos;s removed from{' '}
          {templates.length === 1 ? 'it' : 'all of them'}.
        </Typography>

        {templates.length > SEARCH_THRESHOLD && (
          <Box className="templateSearchWrapper">
            <Input
              className="templateSearchInput"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value.trimStart())}
              placeholder="Search by template id or title"
              startAdornment={
                <InputAdornment position="start">
                  <img src={ContactSearchCIcon} alt="search" />
                </InputAdornment>
              }
              endAdornment={
                search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
          </Box>
        )}

        <Box className="templateList">
          {filteredTemplates.length === 0 ? (
            <Typography component="p" className="templateListEmpty">
              No templates match your search.
            </Typography>
          ) : (
            filteredTemplates.map((template) => (
              <Box key={template.id} className="templateCard">
                <Box className="templateCardInfo">
                  <Typography component="span" className="templateCardId">
                    #{template.id}
                  </Typography>
                  <Typography
                    component="span"
                    className="templateCardTitle"
                    title={template.title || 'Untitled Template'}
                  >
                    {template.title || 'Untitled Template'}
                  </Typography>
                </Box>
                <a
                  className="templateCardLink"
                  href={`/template-builder/${template.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open
                  <OpenInNewIcon />
                </a>
              </Box>
            ))
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LinkedTemplatesModal;
